import { Injectable } from "@angular/core";
import "rxjs/add/operator/map";

// Classes.
import { BaseComponentLocator } from "../locators/base.component-locator";
import { DisciplineLocatorComponent } from "../locators/discipline.locator-component";
import { CategoryLocatorComponent } from "../locators/category.locator-component";
import { CardinalityLocatorComponent } from "../locators/cardinality.locator-component";
import { DateLocatorComponent } from "../locators/date.locator-component";
import { AthleteLocatorComponent } from "../locators/athlete.locator-component";
import { StringLocatorComponent } from "../locators/string.locator-component";
import { LevelLocatorComponent } from "../locators/level.locator-component";
import { WithResultsLocatorComponent } from "../locators/with-results.locator-component";
import { LiveLocatorComponent } from "../locators/live.locator-component";
import { SortingLocatorComponent } from "../locators/sorting.locator-component";
import { DetailedLocatorComponent } from "../locators/detailed.locator-component";
import { CategoryIdLocatorComponent } from "../locators/categoryid.locator-component";
import { YearLocatorComponent } from "../locators/year.locator-component";
import { ParaArcheryLocatorComponent } from "../locators/paraarchery.locator-component";

// Interfaces.
import { LocatorInterface } from "../interfaces/locator.interface";

// Providers.
import { WABaseService } from "./wa-base.service";
import { LoggerService } from "../logger/logger-service";
import { WaHelperService } from "./wa-helper-service";
import { EnvironmentService } from "./environment-service";
import { RankSortingLocatorComponent } from "../locators/indoor-sorting-locator";
import { RecordHistoryListLocator } from "../locators/record-history-list.locator";
import { HttpParams } from '@angular/common/http';

export class ParamSpecStruct {
  constructor(public name: ParamName, public componentName: string) { }
}

export type ParamName =
  | "DisciplineId"
  | "Year"
  | "CatCode"
  | "Team"
  | "Date"
  | "Id"
  | "StartDate"
  | "EndDate"
  | "LevelId"
  | "WithResults"
  | "Live"
  | "fText"
  | "SortBy"
  | "Detailed"
  | "CatId"
  | "RecordLevel"
  | "Historical"
  | "ParaArchery"
  | "RecordType"
  | "Country"
  | "CompId"
  | "Featured"
  | "EventTypeId"
  | "embedFiles"
  | "WorldRankingEvent"
  | "WorldRecordStatus"
  | "DivId"
  | "RecordArea";

// Number of items to request. Can be 'ALL'.
export type RequestedItems = number | "ALL";

@Injectable()
export class ParamBuilderService extends WABaseService {
  constructor(
    protected logger: LoggerService,
    protected helper: WaHelperService,
    protected env: EnvironmentService
  ) {
    super(logger, env);
  }

  public static applyPagintationParams(
    params: HttpParams,
    requestedItems: RequestedItems = 10
  ) {
    params = params.append("RBP", requestedItems + "");
    return params;
  }

  public build(
    paramSpecList: ParamSpecStruct[] = null,
    locator: LocatorInterface = null,
    requestedItems: RequestedItems = 10
  ): HttpParams {
    let params: HttpParams = new HttpParams();

    // We want the protocol's version 3.
    params = params.append("v", "3");

    // Apply pagination params
    params = ParamBuilderService.applyPagintationParams(params, requestedItems);

    if (paramSpecList) {
      for (let paramSpec of paramSpecList) {
        let paramValue = this.processParam(paramSpec, locator);
        // Only use params that are not null.
        if (paramValue !== null) {
          params = params.append(paramSpec.name, paramValue ? paramValue : "0");
        }
      }
    }

    return params;
  }

  protected processParam(
    paramSpec: ParamSpecStruct,
    locator: LocatorInterface
  ) {
    let value: any;
    let component: BaseComponentLocator;
    component = locator.getComponent(paramSpec.componentName);

    try {
      if (component instanceof DisciplineLocatorComponent) {
        value = this.processParamDiscipline(component);
      } else if (component instanceof StringLocatorComponent) {
        value = component.value !== null ? component.toString() : null;
      } else if (component instanceof CategoryLocatorComponent) {
        value = this.processParamCategory(component);
      } else if (component instanceof CardinalityLocatorComponent) {
        value = this.processParamCardinality(component);
      } else if (component instanceof DateLocatorComponent) {
        value = this.processParamDate(component);
      } else if (component instanceof AthleteLocatorComponent) {
        value = this.processParamAthleteId(component);
      } else if (component instanceof LevelLocatorComponent) {
        value = this.processParamLevel(component);
      } else if (component instanceof WithResultsLocatorComponent) {
        value = component.value;
      } else if (component instanceof LiveLocatorComponent) {
        value = component.value;
      } else if (component instanceof SortingLocatorComponent) {
        value = this.processParamSorting(component);
      } else if (component instanceof DetailedLocatorComponent) {
        value = component.value ? "1" : "0";
      } else if (component instanceof CategoryIdLocatorComponent) {
        value = component.value;
      } else if (component instanceof YearLocatorComponent) {
        value = component.value;
      } else if (component instanceof CategoryIdLocatorComponent) {
        value = component.value;
      } else if (component instanceof ParaArcheryLocatorComponent) {
        switch (component.value) {
          case true:
            value = "1";
            break;
          case false:
            value = "0";
            break;
          case "1|0" as any:
            value = "1|0" as any;
            break;
          case null:
            value = null;
            break;
        }
      } else if (component instanceof RankSortingLocatorComponent) {
        value = this.processParamSorting(component as any);
      } else {
        value = component['value'];
        this.warning(
          "Param spec contains an unknown component locator: ",
          paramSpec
        );
      }
    } catch (exception) {
      this.error(
        `Error building params. Error was "${exception}". Context (paramSpec, locator): `,
        paramSpec,
        locator
      );
    }

    return value;
  }
  protected processParaArchery(component: ParaArcheryLocatorComponent) {
    return component.value;
  }
  protected processParamDiscipline(component: DisciplineLocatorComponent) {
    return component.id;
  }

  protected processParamCategory(component: CategoryLocatorComponent) {
    return component.catCode;
  }

  protected processParamCardinality(
    component: CardinalityLocatorComponent
  ): string {
    let value;
    switch (component.value) {
      case true:
        value = "1";
        break;
      case false:
        value = "0";
        break;
      case undefined:
        value = null;
        break;
      case null:
        value = null;
        break;
    }
    return value;
  }

  protected processParamDate(component: DateLocatorComponent) {
    let paramValue: string = null;
    if (component.value) {
      paramValue = this.helper.formatDate(component.value, "webservice");
    }
    return paramValue;
  }

  protected processParamAthleteId(component: AthleteLocatorComponent) {
    return component.id;
  }

  protected processParamLevel(component: LevelLocatorComponent): string {
    let value = null;

    if (component.isGroup) {
      value =
        component.group === "ALL"
          ? null
          : this.helper
            .getLevelIdByLevelGroupName(component.group)
            .map(v => v + "")
            .join("|");
    } else if (component.id) {
      value = component.id + "";
    }

    return value;
  }

  protected processParamSorting(component: SortingLocatorComponent): string {
    return (component.asc ? "" : "-") + component.sortingType.toUpperCase();
  }
}
